import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Eye, Calendar, Clock, MapPin, Image as ImageIcon, Type, Palette, X } from 'lucide-react';
import { toast } from 'react-toastify';
import AlertTemplate from './templates/AlertTemplate';
import PromotionTemplate from './templates/PromotionTemplate';
import EventTemplate from './templates/EventTemplate';
import InfoTemplate from './templates/InfoTemplate';
import WelcomeTemplate from './templates/WelcomeTemplate';
import BlankTemplate from './templates/BlankTemplate';

function AnnouncementManager({ user }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewAnnouncement, setPreviewAnnouncement] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

  // ✅ HELPER: Get complete default styling (prevents undefined errors)
  const getDefaultStyling = () => ({
    backgroundColor: '#ffffff',  // ✅ WHITE background (Blank template default)
    accentColor: '#fca5a5',
    textColor: '#000000',  // ✅ BLACK text (Blank template default)
    fontSize: 'large',
    fontFamily: 'Inter',
    icon: 'alert',
    backgroundImage: '',
    announcementImage: '',
    layoutStyle: 'left',
    headerFont: 'Montserrat',
    bodyFont: 'Raleway',
    footerFont: 'Raleway',
    footerText: "ELLA'S PLACE",
    showLogo: true,  // ✅ ADDED: Logo toggle control
  });

  // ✅ HELPER: Get complete default content
  const getDefaultContent = () => ({
    // Welcome Template
    welcomeTagline: '',
    brandName: '',
    services: ['Salon', 'Spa', 'Ladies Gym'],
    
    // Info Template
    infoBadge: '',
    
    // Event Template
    eventBadge: 'Upcoming Event',
    eventSubtitle: 'Mark Your Calendar',
    eventLocation: "Ella's Place",
    eventTime: 'Coming Soon',
    eventDate: '',  // ✅ Event date field
    eventCTA: 'Save The Date!',
    
    // Promotion Template
    promotionBadge: '',
    promotionTagline: '',
    
    // Alert Template
    alertBadge: 'Important Notice',
    
    // ✅ CTA toggle (default false)
    ctaEnabled: false,
  });

  // ✅ HELPER: Get complete default schedule
  const getDefaultSchedule = () => ({
    startDate: '',
    endDate: '',
    activeDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    startTime: '',  // ✅ Empty = show all day
    endTime: '',    // ✅ Empty = show all day
  });

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    template: 'blank',  // ✅ CHANGED: Default to blank template
    styling: getDefaultStyling(),
    content: getDefaultContent(),
    displayMode: 'mixed',
    duration: 15,
    frequency: 3,
    priority: 5,
    displayLocation: ['gym', 'salon'],
    schedule: getDefaultSchedule(),
    isActive: true,
    ctaEnabled: false,  // ✅ STAGE 5: CTA toggle (default false, true for blank/event)
  });

  const templates = [
    { id: 'blank', name: 'Blank', color: '#ffffff', icon: '📄' },
    { id: 'alert', name: 'Alert', color: '#ef4444', icon: '⚠️' },
    { id: 'promotion', name: 'Promotion', color: '#1a1a1a', icon: '🎉' },
    { id: 'event', name: 'Event', color: '#14b8a6', icon: '📅' },
    { id: 'info', name: 'Info', color: '#3b82f6', icon: 'ℹ️' },
    { id: 'welcome', name: 'Welcome', color: '#5B4E75', icon: '👋' },
  ];

  const fontFamilies = [
    'Inter', 'Roboto', 'Poppins', 'Montserrat', 'Raleway', 
    'Playfair Display', 'Lato', 'Oswald', 'Open Sans', 'Merriweather'
  ];

  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/signage/announcements`);
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateId) => {
    const templateColors = {
      blank: { backgroundColor: '#ffffff', accentColor: '#000000', textColor: '#000000', icon: 'blank' },
      alert: { backgroundColor: '#ef4444', accentColor: '#fca5a5', textColor: '#ffffff', icon: 'alert' },
      promotion: { backgroundColor: '#1a1a1a', accentColor: '#FDB44B', textColor: '#ffffff', icon: 'promotion' },
      event: { backgroundColor: '#14b8a6', accentColor: '#5eead4', textColor: '#ffffff', icon: 'event' },
      info: { backgroundColor: '#3b82f6', accentColor: '#60a5fa', textColor: '#ffffff', icon: 'info' },
      welcome: { backgroundColor: '#5B4E75', accentColor: '#FFFFFF', textColor: '#ffffff', icon: 'welcome' },
    };

    // ✅ NEW: Set message based on template
    let defaultMessage = '';
    if (templateId === 'blank') {
      defaultMessage = `Welcome to <strong>Ella's Place!</strong>

<strong>Today's Classes:</strong>
<ul>
<li>Zumba - 8.00am</li>
<li>Yoga - 1.00pm</li>
<li>Dance class - 7.00pm</li>
</ul>

<em>Let's get you in shape</em>`;
    }

    setFormData({
      ...formData,
      template: templateId,
      message: defaultMessage, // ✅ ADD THIS LINE
      styling: {
        ...formData.styling,
        ...templateColors[templateId],
      },
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // ✅ UPDATED: Support both images AND videos
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      toast.error('Please upload an image or video file');
      return;
    }

    // Size limit: 5MB for images, 50MB for videos
    const maxSize = isImage ? 5 * 1024 * 1024 : 50 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`${isImage ? 'Image' : 'Video'} must be less than ${isImage ? '5MB' : '50MB'}`);
      return;
    }

    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    // ✅ REMOVED: No displayLocation or duration needed for announcement media

    try {
      // ✅ NEW ENDPOINT: /api/signage/announcement-media (separate from playlist media)
      const response = await axios.post(`${API_URL}/api/signage/announcement-media`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData({
        ...formData,
        styling: {
          ...formData.styling,
          announcementImage: response.data.filePath
        }
      });
      
      toast.success(`${isImage ? 'Image' : 'Video'} uploaded successfully`);
    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error(error.response?.data?.message || 'Failed to upload media');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setFormData({
      ...formData,
      styling: {
        ...formData.styling,
        announcementImage: ''
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.message) {
      toast.error('Title and message are required');
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/signage/announcements/${editingId}`, formData);
        toast.success('Announcement updated successfully');
      } else {
        await axios.post(`${API_URL}/api/signage/announcements`, formData);
        toast.success('Announcement created successfully');
      }
      
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast.error('Failed to save announcement');
    }
  };

  const handleEdit = (announcement) => {
    // ✅ Properly merge styling with explicit boolean handling
    const mergedStyling = {
      ...getDefaultStyling(),
      ...announcement.styling,
    };
    
    // ✅ Explicitly preserve boolean fields (they can be false!)
    if (announcement.styling?.showLogo !== undefined) {
      mergedStyling.showLogo = announcement.styling.showLogo;
    }
    
    // ✅ Properly merge content with explicit boolean handling
    const mergedContent = {
      ...getDefaultContent(),
      ...announcement.content,
    };
    
    // ✅ Explicitly preserve boolean fields (they can be false!)
    if (announcement.content?.ctaEnabled !== undefined) {
      mergedContent.ctaEnabled = announcement.content.ctaEnabled;
    }
    
    setFormData({
      title: announcement.title,
      message: announcement.message,
      template: announcement.template,
      styling: mergedStyling,
      content: mergedContent,
      displayMode: announcement.displayMode || 'mixed',
      duration: announcement.duration || 15,
      frequency: announcement.frequency || 3,
      priority: announcement.priority || 5,
      displayLocation: announcement.displayLocation || ['gym', 'salon'],
      schedule: { ...getDefaultSchedule(), ...announcement.schedule },
      isActive: announcement.isActive !== undefined ? announcement.isActive : true,
    });
    setEditingId(announcement._id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;

    try {
      await axios.delete(`${API_URL}/api/signage/announcements/${id}`);
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      toast.error('Failed to delete announcement');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: `Welcome to <strong>Ella's Place!</strong>

<strong>Today's Classes:</strong>
<ul>
<li>Zumba - 8.00am</li>
<li>Yoga - 1.00pm</li>
<li>Dance class - 7.00pm</li>
</ul>

<em>Let's get you in shape</em>`,
      template: 'blank',  // ✅ CHANGED: Default to blank template
      styling: getDefaultStyling(),
      content: getDefaultContent(),
      displayMode: 'mixed',
      duration: 15,
      frequency: 3,
      priority: 5,
      displayLocation: ['gym', 'salon'],
      schedule: getDefaultSchedule(),
      isActive: true,
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handlePreview = (announcement) => {
    setPreviewAnnouncement(announcement || formData);
    setPreviewMode(true);
  };

  const toggleDay = (day) => {
    const days = formData.schedule.activeDays || [];
    const newDays = days.includes(day)
      ? days.filter(d => d !== day)
      : [...days, day];
    
    setFormData({
      ...formData,
      schedule: { ...formData.schedule, activeDays: newDays }
    });
  };

  const renderAnnouncement = (announcement) => {
    const templates = {
      blank: BlankTemplate,
      alert: AlertTemplate,
      promotion: PromotionTemplate,
      event: EventTemplate,
      info: InfoTemplate,
      welcome: WelcomeTemplate,
    };

    const TemplateComponent = templates[announcement.template] || InfoTemplate;
    
    return (
      <TemplateComponent
        title={announcement.title}
        message={announcement.message}
        styling={announcement.styling}
        content={announcement.content}
      />
    );
  };

  return (
    <div className="space-y-6">
      <style>{`
        /* ✅ Style HTML tags in textarea to appear italic and faded */
        textarea.html-editor {
          font-family: 'Courier New', monospace !important;
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Announcements</h2>
          <p className="text-sm text-gray-600 mt-1">Create and manage TV announcements</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 font-medium"
        >
          <Plus className="h-5 w-5" />
          {showForm ? 'Cancel' : 'Create Announcement'}
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* CREATE/EDIT FORM - NEW LAYOUT: 60% Preview + 40% Form          */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-purple-200">
          <h3 className="text-xl font-bold mb-6 text-gray-900">
            {editingId ? '✏️ Edit Announcement' : '✨ Create Announcement'}
          </h3>

          <form onSubmit={handleSubmit}>
            {/* ✅ CORRECTED LAYOUT: 70/30 Split (Preview 70%, Form 30%) */}
            <div className="grid grid-cols-10 gap-6">
              
              {/* ═══════════════════════════════════════════════════════════ */}
              {/* LEFT PANEL (7/10 = 70%) - Template Selector + Preview */}
              {/* ═══════════════════════════════════════════════════════════ */}
              <div className="col-span-7 space-y-4">
                
                {/* Template Selector */}
                <div>
                  <label className="block text-sm font-semibold mb-3 text-gray-700">
                    📋 Select Template
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {templates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => handleTemplateChange(template.id)}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          formData.template === template.id
                            ? 'border-purple-600 bg-purple-50 shadow-md'
                            : 'border-gray-300 bg-white hover:border-purple-400 hover:shadow-sm'
                        }`}
                        style={{
                          backgroundColor: formData.template === template.id 
                            ? undefined 
                            : template.id === 'blank' ? '#f9fafb' : undefined
                        }}
                      >
                        <div className="text-3xl mb-2">{template.icon}</div>
                        <div className={`text-sm font-medium ${
                          formData.template === template.id ? 'text-purple-700' : 'text-gray-700'
                        }`}>
                          {template.name}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Live Preview - BIG TV-LIKE ASPECT RATIO */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-semibold text-gray-700">
                      📺 Live Preview
                    </label>
                    <button
                      type="button"
                      onClick={() => handlePreview(null)}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-xs font-medium"
                    >
                      <Eye className="h-3 w-3" />
                      Full Screen
                    </button>
                  </div>
                  
                  {/* Preview Container - 16:9 Aspect Ratio */}
                  <div 
                    className="border-4 border-gray-300 rounded-lg overflow-hidden bg-gray-900 shadow-xl"
                    style={{ 
                      aspectRatio: '16 / 9',
                      minHeight: '350px',  // ✅ REDUCED: Was 450px, now 350px to prevent overflow
                      maxHeight: '500px',  // ✅ ADDED: Cap maximum height
                      position: 'relative' 
                    }}
                  >
                    {renderAnnouncement(formData)}
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    ✨ Changes update instantly • 16:9 TV aspect ratio
                  </p>
                </div>

              </div>

              {/* ═══════════════════════════════════════════════════════════ */}
              {/* RIGHT PANEL (3/10 = 30%) - Form Fields (Scrollable)   */}
              {/* ═══════════════════════════════════════════════════════════ */}
              <div className="col-span-3">
                <div 
                  className="space-y-4 overflow-y-auto pr-2" 
                  style={{ maxHeight: '750px' }}
                >
                  
                  {/* Content Section */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-bold mb-3 text-purple-700 flex items-center gap-2">
                      <Type className="h-4 w-4" />
                      Content
                    </h4>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium mb-1 text-gray-700">Header Title *</label>
                        <input
                          type="text"
                          required
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Enter title..."
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium mb-1 text-gray-700">Body Text *</label>
                        <textarea
                          required
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          rows={6}
                          className="html-editor w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Enter message with HTML formatting..."
                          style={{
                            fontFamily: "'Courier New', monospace",
                            background: 'linear-gradient(to right, #f9fafb 0%, #ffffff 100%)',
                            lineHeight: '1.5',
                          }}
                        />
                        {formData.template === 'blank' && (
  <p className="text-xs text-gray-500 mt-1">
    💡 HTML supported: <code>...</code>
  </p>
)}
                      </div>

                      {/* Blank Template - Footer Text */}
                      {formData.template === 'blank' && (
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">Footer Text</label>
                          <input
                            type="text"
                            value={formData.styling.footerText || "ELLA'S PLACE"}
                            onChange={(e) => setFormData({
                              ...formData,
                              styling: { ...formData.styling, footerText: e.target.value }
                            })}
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Footer text..."
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ✅ STAGE 5: CTA Toggle Section */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-bold mb-3 text-purple-700">
                      Call-to-Action
                    </h4>
                    <div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.content?.ctaEnabled === true}
                          onChange={(e) => setFormData({
                            ...formData,
                            content: { ...formData.content, ctaEnabled: e.target.checked }
                          })}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="text-xs font-medium text-gray-700">
                          Enable WhatsApp QR Code
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-6">
                        Show QR code for customers to contact via WhatsApp (+234 905 544 4495)
                      </p>
                    </div>
                  </div>

                  {/* ✅ STAGE 5 & 6: Event Details Section (Conditional) */}
                  {formData.template === 'event' && (
                    <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg p-4 border-2 border-teal-200">
                      <h4 className="text-sm font-bold mb-3 text-teal-700 flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Event Details
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">Event Date</label>
                          <input
                            type="date"
                            value={formData.content?.eventDate || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              content: { ...formData.content, eventDate: e.target.value }
                            })}
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Date will be displayed with styled calendar icon
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">Event Time</label>
                          <input
                            type="text"
                            value={formData.content?.eventTime || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              content: { ...formData.content, eventTime: e.target.value }
                            })}
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            placeholder="e.g., 6:00 PM - 9:00 PM"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">Event Location</label>
                          <input
                            type="text"
                            value={formData.content?.eventLocation || "Ella's Place"}
                            onChange={(e) => setFormData({
                              ...formData,
                              content: { ...formData.content, eventLocation: e.target.value }
                            })}
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            placeholder="Ella's Place"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Design Controls */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-bold mb-3 text-purple-700 flex items-center gap-2">
                      <Palette className="h-4 w-4" />
                      Design
                    </h4>

                    <div className="space-y-3">
                      {/* Blank Template - Fonts */}
                      {formData.template === 'blank' && (
                        <>
                          <div>
                            <label className="block text-xs font-medium mb-1 text-gray-700">Header Font</label>
                            <select
                              value={formData.styling.headerFont || 'Montserrat'}
                              onChange={(e) => setFormData({
                                ...formData,
                                styling: { ...formData.styling, headerFont: e.target.value }
                              })}
                              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            >
                              {fontFamilies.map(font => (
                                <option key={font} value={font}>{font}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-medium mb-1 text-gray-700">Body Font</label>
                            <select
                              value={formData.styling.bodyFont || 'Raleway'}
                              onChange={(e) => setFormData({
                                ...formData,
                                styling: { ...formData.styling, bodyFont: e.target.value }
                              })}
                              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            >
                              {fontFamilies.map(font => (
                                <option key={font} value={font}>{font}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-medium mb-1 text-gray-700">Footer Font</label>
                            <select
                              value={formData.styling.footerFont || 'Raleway'}
                              onChange={(e) => setFormData({
                                ...formData,
                                styling: { ...formData.styling, footerFont: e.target.value }
                              })}
                              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            >
                              {fontFamilies.map(font => (
                                <option key={font} value={font}>{font}</option>
                              ))}
                            </select>
                          </div>

                          {/* ✅ ADDED: Show Logo Toggle */}
                          <div>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.styling.showLogo !== false}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  styling: { ...formData.styling, showLogo: e.target.checked }
                                })}
                                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                              />
                              <span className="text-xs font-medium text-gray-700">
                                Show Ella's Logo (top right)
                              </span>
                            </label>
                            <p className="text-xs text-gray-500 mt-1 ml-6">
                              Display logo watermark on announcement
                            </p>
                          </div>
                        </>
                      )}

                      {/* Colors */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">Background</label>
                          <input
                            type="color"
                            value={formData.styling.backgroundColor || '#ef4444'}
                            onChange={(e) => setFormData({
                              ...formData,
                              styling: { ...formData.styling, backgroundColor: e.target.value }
                            })}
                            className="w-full h-10 border rounded-lg cursor-pointer"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">Text Color</label>
                          <input
                            type="color"
                            value={formData.styling.textColor || '#ffffff'}
                            onChange={(e) => setFormData({
                              ...formData,
                              styling: { ...formData.styling, textColor: e.target.value }
                            })}
                            className="w-full h-10 border rounded-lg cursor-pointer"
                          />
                        </div>
                      </div>

                      {/* Font Size */}
                      <div>
                        <label className="block text-xs font-medium mb-1 text-gray-700">Font Size</label>
                        <select
                          value={formData.styling.fontSize || 'large'}
                          onChange={(e) => setFormData({
                            ...formData,
                            styling: { ...formData.styling, fontSize: e.target.value }
                          })}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Image Upload */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-bold mb-3 text-purple-700 flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" />
                      Image (Optional)
                    </h4>

                        {formData.styling.announcementImage ? (
                      <div className="relative">
                        {formData.styling.announcementImage.endsWith('.mp4') || 
                         formData.styling.announcementImage.endsWith('.webm') || 
                         formData.styling.announcementImage.endsWith('.ogg') ? (
                          <video
                            src={`${API_URL}${formData.styling.announcementImage}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-purple-200"
                            controls
                          />
                        ) : (
                          <img
                            src={`${API_URL}${formData.styling.announcementImage}`}
                            alt="Announcement"
                            className="w-full h-32 object-cover rounded-lg border-2 border-purple-200"
                          />
                        )}
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>   
                    ) : (
                      <label className="block cursor-pointer">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-purple-400 hover:bg-purple-50 transition-all text-center">
                          <ImageIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-xs text-gray-600 font-medium">Click to upload image/video</p>
                          <p className="text-xs text-gray-400 mt-1">Images: 5MB • Videos: 50MB</p>
                        </div>
                        <input
                          type="file"
                          accept="image/*,video/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploadingImage}
                        />
                      </label>
                    )}
                    {uploadingImage && (
                      <p className="text-xs text-purple-600 mt-2 text-center">Uploading...</p>
                    )}
                  </div>

                  {/* Display Settings */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-bold mb-3 text-purple-700">Display Settings</h4>

                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">Duration (sec)</label>
                          <input
                            type="number"
                            min="5"
                            max="60"
                            value={formData.duration}
                            onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">Priority (1-10)</label>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={formData.priority}
                            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium mb-1 text-gray-700">Display Mode</label>
                        <select
                          value={formData.displayMode}
                          onChange={(e) => setFormData({ ...formData, displayMode: e.target.value })}
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="mixed">Mixed (with media)</option>
                          <option value="exclusive">Exclusive (only this)</option>
                        </select>
                      </div>

                      {formData.displayMode === 'mixed' && (
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">
                            Frequency (every X items)
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={formData.frequency}
                            onChange={(e) => setFormData({ ...formData, frequency: parseInt(e.target.value) })}
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-xs font-medium mb-2 text-gray-700">Locations</label>
                        <div className="flex gap-3">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.displayLocation.includes('gym')}
                              onChange={(e) => {
                                const locations = e.target.checked
                                  ? [...formData.displayLocation, 'gym']
                                  : formData.displayLocation.filter(l => l !== 'gym');
                                setFormData({ ...formData, displayLocation: locations });
                              }}
                              className="rounded"
                            />
                            <span className="text-sm">Gym</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.displayLocation.includes('salon')}
                              onChange={(e) => {
                                const locations = e.target.checked
                                  ? [...formData.displayLocation, 'salon']
                                  : formData.displayLocation.filter(l => l !== 'salon');
                                setFormData({ ...formData, displayLocation: locations });
                              }}
                              className="rounded"
                            />
                            <span className="text-sm">Salon</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Schedule */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-bold mb-3 text-purple-700 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Schedule (Optional)
                    </h4>

                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">Start Date</label>
                          <input
                            type="date"
                            value={formData.schedule.startDate}
                            onChange={(e) => setFormData({
                              ...formData,
                              schedule: { ...formData.schedule, startDate: e.target.value }
                            })}
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">End Date</label>
                          <input
                            type="date"
                            value={formData.schedule.endDate}
                            onChange={(e) => setFormData({
                              ...formData,
                              schedule: { ...formData.schedule, endDate: e.target.value }
                            })}
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium mb-2 text-gray-700">Active Days</label>
                        <div className="flex flex-wrap gap-2">
                          {daysOfWeek.map((day) => (
                            <button
                              key={day}
                              type="button"
                              onClick={() => toggleDay(day)}
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                formData.schedule.activeDays.includes(day)
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-gray-200 text-gray-700'
                              }`}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">Start Time</label>
                          <input
                            type="time"
                            value={formData.schedule.startTime}
                            onChange={(e) => setFormData({
                              ...formData,
                              schedule: { ...formData.schedule, startTime: e.target.value }
                            })}
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-700">End Time</label>
                          <input
                            type="time"
                            value={formData.schedule.endTime}
                            onChange={(e) => setFormData({
                              ...formData,
                              schedule: { ...formData.schedule, endTime: e.target.value }
                            })}
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 border-t pt-6 mt-6">
              <button
                type="submit"
                className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium shadow-md hover:shadow-lg transition-all"
              >
                {editingId ? '💾 Update' : '✨ Create'} Announcement
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Announcements List */}
      <div>
        <h3 className="text-lg font-bold mb-4">Active Announcements</h3>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : announcements.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed">
            <div className="text-5xl mb-3">📢</div>
            <p className="text-lg font-medium">No announcements yet</p>
            <p className="text-sm mt-2">Create your first announcement to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {announcements.map((announcement) => (
              <div
                key={announcement._id}
                className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                  !announcement.isActive ? 'opacity-60 bg-gray-50' : 'bg-white'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">
                        {templates.find(t => t.id === announcement.template)?.icon}
                      </span>
                      <h4 className="font-bold text-gray-900">{announcement.title}</h4>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{announcement.message}</p>
                  </div>
                </div>

                {announcement.styling?.announcementImage && (
                  announcement.styling.announcementImage.endsWith('.mp4') || 
                  announcement.styling.announcementImage.endsWith('.webm') || 
                  announcement.styling.announcementImage.endsWith('.ogg') ? (
                    <video
                      src={`${API_URL}${announcement.styling.announcementImage}`}
                      className="w-full h-32 object-cover rounded mb-3"
                      controls
                    />
                  ) : (
                    <img
                      src={`${API_URL}${announcement.styling.announcementImage}`}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded mb-3"
                    />
                  )
                )}

                <div className="space-y-1 mb-3 text-xs text-gray-600">
                  <div className="flex items-center">
                    <Clock className="h-3 w-3 mr-1" />
                    <span>{announcement.duration}s | {announcement.displayMode}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="h-3 w-3 mr-1" />
                    <span>{announcement.displayLocation.join(', ')}</span>
                  </div>
                  {announcement.schedule?.startDate && (
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      <span>Scheduled</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handlePreview(announcement)}
                    className="flex-1 px-3 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200 flex items-center justify-center gap-1"
                  >
                    <Eye className="h-3 w-3" />
                    Preview
                  </button>
                  <button
                    onClick={() => handleEdit(announcement)}
                    className="flex-1 px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 flex items-center justify-center gap-1"
                  >
                    <Edit2 className="h-3 w-3" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(announcement._id, announcement.title)}
                    className="flex-1 px-3 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200 flex items-center justify-center gap-1"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* TV Preview Modal */}
      {previewMode && previewAnnouncement && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-8">
          <div className="relative bg-black rounded-xl shadow-2xl" style={{ width: '90vw', height: '90vh', maxWidth: '1600px', maxHeight: '900px' }}>
            <button
              onClick={() => setPreviewMode(false)}
              className="absolute -top-12 right-0 px-4 py-2 bg-white text-gray-900 rounded-lg hover:bg-gray-100 flex items-center gap-2 font-medium"
            >
              <X className="h-5 w-5" />
              Close Preview
            </button>
            <div className="w-full h-full overflow-hidden rounded-xl">
              {renderAnnouncement(previewAnnouncement)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnnouncementManager;